import React, { useState, useEffect, useMemo } from 'react';
import { X, DollarSign, Download, Printer, Search, Filter, Users, AlertCircle, TrendingUp } from 'lucide-react';

const BusBalanceReportModal = ({ isOpen, onClose, apiCall, selectedYear, showError, showSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    academicYear: selectedYear,
    minBalance: '',
    trimester: ''
  });

  // Load report data
  const loadReport = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (filters.academicYear) params.append('academicYear', filters.academicYear);
      if (filters.minBalance) params.append('minBalance', filters.minBalance);
      if (filters.trimester) params.append('trimester', filters.trimester);
      
      const data = await apiCall(`/bus/balance-report?${params.toString()}`);
      setReportData(data.families || []);
      setSummary(data.summary || {});
    } catch (error) {
      showError('Failed to load balance report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadReport();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filters.academicYear, filters.trimester, filters.minBalance]);

  // Client-side search filtering
  const filteredData = useMemo(() => {
    if (!searchTerm) return reportData;
    
    const term = searchTerm.toLowerCase();
    return reportData.filter(family => 
      family.FamilyID?.toLowerCase().includes(term) ||
      family.FatherName?.toLowerCase().includes(term) ||
      family.PrimaryPhone?.includes(term)
    );
  }, [reportData, searchTerm]);

  const handlePrint = () => {
    const printContent = document.getElementById('balance-report-print');
    const winPrint = window.open('', '', 'width=900,height=650');
    winPrint.document.write(`
      <html>
        <head>
          <title>Bus Balance Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; margin-bottom: 20px; }
            .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 20px; }
            .summary-card { border: 2px solid #e5e7eb; padding: 15px; border-radius: 8px; }
            .summary-card h3 { margin: 0 0 10px 0; font-size: 12px; color: #6b7280; }
            .summary-card p { margin: 0; font-size: 24px; font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f3f4f6; font-weight: bold; }
            .balance { color: #dc2626; font-weight: bold; }
            tfoot { background-color: #f9fafb; font-weight: bold; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    winPrint.document.close();
    winPrint.focus();
    setTimeout(() => {
      winPrint.print();
      winPrint.close();
    }, 250);
  };

  const handleExport = () => {
    // Create CSV
    const headers = ['Family ID', 'Father Name', 'Phone', 'Students', 'Total Charges', 'Total Paid', 'Balance Remaining'];
    const rows = filteredData.map(family => [
      family.FamilyID,
      family.FatherName,
      family.PrimaryPhone,
      family.StudentCount,
      family.TotalCharges.toFixed(2),
      family.TotalPaid.toFixed(2),
      family.BalanceRemaining.toFixed(2)
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bus-balance-report-${filters.academicYear}.csv`;
    a.click();
    
    showSuccess('Report exported successfully');
  };

  const formatUSD = (amount) => {
    return '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Bus Balance Report</h2>
            <p className="text-sm text-gray-600 mt-1">Families with outstanding bus fees</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              disabled={loading || filteredData.length === 0}
            >
              <Download size={18} />
              Export CSV
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              disabled={loading || filteredData.length === 0}
            >
              <Printer size={18} />
              Print
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              {summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">Total Families</p>
                        <p className="text-3xl font-bold text-gray-800 mt-1">{summary.TotalFamilies || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">with balances</p>
                      </div>
                      <div className="bg-blue-200 p-3 rounded-lg">
                        <Users className="text-blue-700" size={24} />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">Total Charges</p>
                        <p className="text-3xl font-bold text-gray-800 mt-1">
                          {formatUSD(summary.TotalCharges || 0)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">all families</p>
                      </div>
                      <div className="bg-purple-200 p-3 rounded-lg">
                        <DollarSign className="text-purple-700" size={24} />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">Total Paid</p>
                        <p className="text-3xl font-bold text-green-700 mt-1">
                          {formatUSD(summary.TotalPaid || 0)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">collected</p>
                      </div>
                      <div className="bg-green-200 p-3 rounded-lg">
                        <TrendingUp className="text-green-700" size={24} />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border-2 border-red-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">Outstanding</p>
                        <p className="text-3xl font-bold text-red-700 mt-1">
                          {formatUSD(summary.TotalOutstanding || 0)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">to collect</p>
                      </div>
                      <div className="bg-red-200 p-3 rounded-lg">
                        <AlertCircle className="text-red-700" size={24} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <Filter size={18} className="text-gray-600" />
                  <h3 className="font-semibold text-gray-800">Filters</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
                    <input
                      type="text"
                      value={filters.academicYear}
                      onChange={(e) => setFilters({...filters, academicYear: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="25-26"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trimester</label>
                    <select
                      value={filters.trimester}
                      onChange={(e) => setFilters({...filters, trimester: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">All Trimesters</option>
                      <option value="1">Trimester 1</option>
                      <option value="2">Trimester 2</option>
                      <option value="3">Trimester 3</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Balance</label>
                    <input
                      type="number"
                      value={filters.minBalance}
                      onChange={(e) => setFilters({...filters, minBalance: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Search size={14} className="inline mr-1" />
                      Search
                    </label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Family, name, phone..."
                    />
                    {searchTerm && (
                      <p className="text-xs text-gray-500 mt-1">
                        Found {filteredData.length} of {reportData.length}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Printable Content */}
              <div id="balance-report-print">
                <h1 style={{textAlign: 'center', marginBottom: '20px', display: 'none'}}>
                  Bus Balance Report - {filters.academicYear}
                </h1>
                
                {/* Summary for Print */}
                <div className="summary" style={{display: 'none'}}>
                  <div className="summary-card">
                    <h3>Total Families</h3>
                    <p>{summary?.TotalFamilies || 0}</p>
                  </div>
                  <div className="summary-card">
                    <h3>Total Charges</h3>
                    <p>{formatUSD(summary?.TotalCharges || 0)}</p>
                  </div>
                  <div className="summary-card">
                    <h3>Total Paid</h3>
                    <p>{formatUSD(summary?.TotalPaid || 0)}</p>
                  </div>
                  <div className="summary-card">
                    <h3>Outstanding</h3>
                    <p>{formatUSD(summary?.TotalOutstanding || 0)}</p>
                  </div>
                </div>

                {/* Report Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-100 border-b">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">#</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Family ID</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Father Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Phone</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Students</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Charges</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Paid</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((family, index) => (
                          <tr key={family.FamilyID} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                            <td className="px-4 py-3">
                              <span className="font-mono text-sm text-blue-600">{family.FamilyID}</span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-semibold text-sm text-gray-800">{family.FatherName}</p>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{family.PrimaryPhone}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                {family.StudentCount}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-semibold">{formatUSD(family.TotalCharges)}</td>
                            <td className="px-4 py-3 text-right text-sm text-green-600 font-semibold">{formatUSD(family.TotalPaid)}</td>
                            <td className="px-4 py-3 text-right">
                              <span className="balance text-red-600 font-bold">
                                {formatUSD(family.BalanceRemaining)}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {filteredData.length === 0 && (
                          <tr>
                            <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                              {searchTerm 
                                ? `No families found matching "${searchTerm}"` 
                                : 'No families with outstanding balances'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {filteredData.length > 0 && (
                        <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                          <tr>
                            <td colSpan="4" className="px-4 py-3 text-right text-sm font-bold text-gray-800">TOTALS:</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-bold">
                                {filteredData.reduce((sum, f) => sum + f.StudentCount, 0)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-bold text-gray-800">
                              {formatUSD(filteredData.reduce((sum, f) => sum + f.TotalCharges, 0))}
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-bold text-green-600">
                              {formatUSD(filteredData.reduce((sum, f) => sum + f.TotalPaid, 0))}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-red-600">
                              {formatUSD(filteredData.reduce((sum, f) => sum + f.BalanceRemaining, 0))}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>

      <style jsx>{`
        @media print {
          #balance-report-print h1 { display: block !important; }
          #balance-report-print .summary { display: grid !important; }
        }
      `}</style>
    </div>
  );
};

export default BusBalanceReportModal;
